<template>
  <div class="user-page">
    <div class="overview-grid" v-loading="overviewLoading">
      <div v-for="item in overviewCards" :key="item.key" class="overview-card">
        <div class="overview-card__label">{{ item.label }}</div>
        <div class="overview-card__value">{{ item.value }}</div>
      </div>
    </div>

    <tableComponents
      ref="tableComponents"
      :pagination="{
        currentPage: paginationForm.page,
        pageSize: paginationForm.page_size,
        total,
      }"
      :searchData="userSearchData"
      :isHaveExport="false"
      :isHaveAdd="false"
      @handleCurrentChange="handleCurrentChange"
      @handleSizeChange="handleSizeChange"
      @sendUserSearch="getSearch"
      @toAdd="showEditDialog"
    >
      <template #table>
        <el-table v-if="!isMobileViewport" :data="list" border stripe v-loading="loading">
          <el-table-column prop="id" label="用户ID" width="80" align="center" />

          <el-table-column label="头像" width="84" align="center">
            <template slot-scope="{ row }">
              <img :src="getAvatar(row.avatar)" alt="" class="avatar" />
            </template>
          </el-table-column>

          <el-table-column label="用户信息" min-width="180">
            <template slot-scope="{ row }">
              <div class="user-main">
                <div class="user-main__title">{{ row.nickname || "--" }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="归属信息" min-width="180">
            <template slot-scope="{ row }">
              <div class="info-lines">
                <div>APP：{{ row.app_name || "--" }}</div>
                <div>渠道：{{ row.channel_name || "--" }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="今日广告" width="110" align="center">
            <template slot-scope="{ row }">
              {{ row.today_total_ad_count || 0 }}
            </template>
          </el-table-column>

          <el-table-column label="今日收益" min-width="140" align="center">
            <template slot-scope="{ row }">
              {{ row.today_total_ecpm_display || "--" }}
            </template>
          </el-table-column>

          <el-table-column label="在线" width="90" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="row.online ? 'success' : 'info'">
                {{ row.online ? "在线" : "离线" }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="风险概览">
            <template slot-scope="{ row }">
              <div class="info-lines">
                <div class="risk-line">
                  <el-tag :type="riskTagType(row.risk_level)" size="mini">
                    {{ riskLevelText(row.risk_level) }}
                  </el-tag>
                  <span v-if="row.cross_app_device_hit">
                    跨App：{{
                      row.cross_app_device_hit
                        ? `是(${row.cross_app_app_count || 0})`
                        : "否"
                    }}
                  </span>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="封禁时间" align="center">
            <template slot-scope="{ row }">
              <el-tag
                v-if="!row.banned_time || row.banned_time * 1000 <= Date.now()"
                type="success"
                size="mini"
              >
                正常
              </el-tag>
              <span v-else>
                {{ formatTime(row.banned_time) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="注册时间" min-width="160" align="center">
            <template slot-scope="{ row }">
              {{ formatTime(row.created_time) }}
            </template>
          </el-table-column>

          <el-table-column label="最后登录" min-width="160" align="center">
            <template slot-scope="{ row }">
              {{ formatTime(row.last_login_time) }}
            </template>
          </el-table-column>

          <el-table-column
            label="操作"
            :width="isMobileViewport ? 76 : 150"
            align="center"
            fixed="right"
          >
            <template slot-scope="{ row }">
              <el-dropdown trigger="click" @command="handleActionCommand($event, row)">
                <el-button
                  size="mini"
                  type="primary"
                  :class="{ 'mobile-action-trigger': isMobileViewport }"
                  :aria-label="isMobileViewport ? '更多操作' : null"
                >
                  <i v-if="isMobileViewport" class="el-icon-more"></i>
                  <template v-else>
                    更多操作<i class="el-icon-arrow-down el-icon--right"></i>
                  </template>
                </el-button>
                <el-dropdown-menu slot="dropdown" class="mobile-action-menu">
                  <el-dropdown-item command="detail">详情</el-dropdown-item>
                  <el-dropdown-item command="accessad">广告记录</el-dropdown-item>
                  <el-dropdown-item command="adlog">其他广告</el-dropdown-item>
                  <el-dropdown-item command="bill">流水</el-dropdown-item>
                  <el-dropdown-item command="risk">风险详情</el-dropdown-item>
                  <el-dropdown-item command="login">登录轨迹</el-dropdown-item>
                  <el-dropdown-item command="ban">
                    {{
                      row.banned_time && row.banned_time * 1000 > Date.now()
                        ? "解除封禁"
                        : "封禁"
                    }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
        <mobile-record-list
          v-else
          :rows="list"
          :loading="loading"
          :title="getMobileUserTitle"
          :subtitle="getMobileUserSubtitle"
          :fields="mobileUserFields"
          :actions="getMobileUserActions"
          :summary-limit="3"
          :summary-columns="3"
          @action="handleActionCommand"
        />
      </template>
    </tableComponents>

    <Dialog
      ref="userDialogRef"
      :formData="formData"
      dialogWidth="520px"
      :keySpan="5"
      :valueSpan="19"
      @onSave="submitForm"
    />

    <user-login-list ref="loginListRef"></user-login-list>
    <user-bill-list ref="billListRef"></user-bill-list>
    <user-ad-list ref="adListRef"></user-ad-list>
    <user-ad-log-list ref="adLogListRef"></user-ad-log-list>
    <user-risk-dialog ref="riskDialogRef"></user-risk-dialog>

    <el-dialog
      title="用户详情"
      :visible.sync="showUserDialog"
      width="50%"
      :close-on-click-modal="false"
      class="user-detail-dialog"
    >
      <UserEdit v-if="showUserDialog" :editId="currentUserId" />
    </el-dialog>
  </div>
</template>

<script>
import Dialog from "@/components/Dialog.vue";
import UserEdit from "./components/UserEdit.vue";
import UserLoginList from "./components/UserLoginList.vue";
import UserBillList from "./components/UserBillList.vue";
import UserAdList from "@/comment/userAdList.vue";
import UserAdLogList from "@/comment/userAdLogList.vue";
import UserRiskDialog from "./components/UserRiskDialog.vue";
import responsiveMixin from "@/mixins/responsive";

const emptyOverview = () => ({
  total_user_count: 0,
  today_new_user_count: 0,
  today_active_user_count: 0,
  today_ad_user_count: 0,
  today_banned_user_count: 0,
  today_income_user_count: 0,
});

export default {
  mixins: [responsiveMixin],
  components: {
    Dialog,
    UserEdit,
    UserLoginList,
    UserBillList,
    UserAdList,
    UserAdLogList,
    UserRiskDialog,
  },
  data() {
    return {
      loading: false,
      overviewLoading: false,
      showUserDialog: false,
      list: [],
      total: 0,
      queryForm: {},
      currentUserId: "",
      overviewStats: emptyOverview(),
      paginationForm: {
        page: 1,
        page_size: 10,
      },
      formData: [
        {
          type: "text",
          key: "username",
          placeholder: "请输入用户名",
          label: "用户名",
          rules: [{ required: true, message: "请输入用户名", trigger: "blur" }],
        },
        {
          type: "text",
          key: "nickname",
          placeholder: "请输入用户昵称",
          label: "用户昵称",
          rules: [
            { required: true, message: "请输入用户昵称", trigger: "blur" },
          ],
        },
        {
          type: "select",
          key: "app_id",
          placeholder: "请选择 APP",
          label: "APP",
          option: this.$store.getters.getAppListGetter,
          rules: [{ required: true, message: "请选择 APP", trigger: "change" }],
        },
        {
          type: "text",
          key: "open_id",
          placeholder: "请输入 open_id",
          label: "Open ID",
        },
        {
          type: "text",
          key: "android_id",
          placeholder: "请输入 Android ID",
          label: "Android ID",
        },
        {
          type: "text",
          key: "ip",
          placeholder: "请输入 IP",
          label: "IP",
        },
        {
          type: "text",
          key: "session_id",
          placeholder: "请输入 session_id",
          label: "Session ID",
        },
        {
          type: "text",
          key: "phone_version",
          placeholder: "请输入系统版本",
          label: "系统版本",
        },
        {
          type: "text",
          key: "sdk_version",
          placeholder: "请输入 SDK 版本",
          label: "SDK版本",
        },
        {
          type: "text",
          key: "phone_manufacturer",
          placeholder: "请输入手机品牌",
          label: "手机品牌",
        },
        {
          type: "datetime",
          key: "banned_time",
          placeholder: "请选择封禁到期时间",
          label: "封禁到期",
        },
        {
          type: "text",
          key: "channel_id",
          placeholder: "请输入渠道ID",
          label: "渠道ID",
        },
        {
          type: "text",
          key: "address",
          placeholder: "请输入地址",
          label: "地址",
        },
      ],
    };
  },
  computed: {
    mobileUserFields() {
      return [
        { label: "今日收益", value: (row) => row.today_total_ecpm_display },
        { label: "渠道ID", key: "channel_id" },
        { label: "今日广告", value: (row) => row.today_total_ad_count || 0 },
        { label: "APP", value: (row) => row.app_name, summary: false },
        { label: "渠道", value: (row) => row.channel_name, summary: false },
        { label: "用户ID", key: "id", summary: false },
        {
          label: "在线状态",
          value: (row) => (row.online ? "在线" : "离线"),
          tag: true,
          tagType: (row) => (row.online ? "success" : "info"),
          summary: false,
        },
        {
          label: "风险等级",
          value: (row) => this.riskLevelText(row.risk_level),
          tag: true,
          tagType: (row) => this.riskTagType(row.risk_level),
          summary: false,
        },
        {
          label: "跨App设备",
          value: (row) =>
            row.cross_app_device_hit
              ? `是（${row.cross_app_app_count || 0}）`
              : "否",
          summary: false,
        },
        {
          label: "封禁状态",
          value: (row) =>
            !row.banned_time || row.banned_time * 1000 <= Date.now()
              ? "正常"
              : `封禁至 ${this.formatTime(row.banned_time)}`,
          summary: false,
        },
        {
          label: "注册时间",
          value: (row) => this.formatTime(row.created_time),
          summary: false,
        },
        {
          label: "最后登录",
          value: (row) => this.formatTime(row.last_login_time),
          summary: false,
        },
      ];
    },
    overviewCards() {
      return [
        {
          key: "total_user_count",
          label: "总用户",
          value: this.overviewStats.total_user_count || 0,
        },
        {
          key: "today_new_user_count",
          label: "今日新增",
          value: this.overviewStats.today_new_user_count || 0,
        },
        {
          key: "today_active_user_count",
          label: "今日活跃",
          value: this.overviewStats.today_active_user_count || 0,
        },
        {
          key: "today_ad_user_count",
          label: "今日广告用户",
          value: this.overviewStats.today_ad_user_count || 0,
        },
        {
          key: "today_banned_user_count",
          label: "今日封禁",
          value: this.overviewStats.today_banned_user_count || 0,
        },
        {
          key: "today_income_user_count",
          label: "今日收入用户",
          value: this.overviewStats.today_income_user_count || 0,
        },
      ];
    },
    userSearchData() {
      return () => [
        {
          title: "",
          type: "1",
          data: [
            {
              type: "date",
              arr: [],
              key: ["start_time", "end_time"],
              placeholder: "注册时间",
            },
            {
              type: "date",
              arr: [],
              key: ["last_time_start", "last_time_end"],
              placeholder: "最后登录时间",
            },
            {
              type: "date",
              arr: [],
              key: ["banned_time_start", "banned_time_end"],
              placeholder: "封禁时间",
            },
          ],
        },
        {
          title: "",
          type: "2",
          data: [
            { type: "text", key: "id", placeholder: "搜索用户ID" },
            { type: "text", key: "nickname", placeholder: "搜索用户昵称" },
            { type: "text", key: "channel_id", placeholder: "搜索渠道ID" },
            {
              type: "select",
              key: "app_id",
              option: this.$store.getters.getAppListGetter,
              placeholder: "所属APP",
              onChange: () => {},
            },
            {
              type: "select",
              key: "online",
              option: [
                { id: "1", title: "在线" },
                { id: "0", title: "离线" },
              ],
              placeholder: "在线状态",
              onChange: () => {},
            },
            {
              type: "select",
              key: "risk_level",
              option: [
                { id: "safe", title: "安全" },
                { id: "low", title: "低风险" },
                { id: "medium", title: "中风险" },
                { id: "high", title: "高风险" },
              ],
              placeholder: "风险等级",
              onChange: () => {},
            },
            {
              type: "select",
              key: "cross_app_device_hit",
              option: [
                { id: "1", title: "跨App设备" },
                { id: "0", title: "非跨App设备" },
              ],
              placeholder: "跨App设备",
              onChange: () => {},
            },
            {
              type: "select",
              key: "is_risk_user",
              option: [
                { id: "1", title: "风险用户" },
                { id: "0", title: "非风险用户" },
              ],
              placeholder: "风险用户",
              onChange: () => {},
            },
          ],
        },
      ];
    },
  },
  methods: {
    getMobileUserTitle(row) {
      return row.nickname || `用户 #${row.id}`;
    },
    getMobileUserSubtitle(row) {
      return `用户ID ${row.id}`;
    },
    getMobileUserActions(row) {
      return [
        { key: "detail", label: "详情", type: "primary" },
        { key: "accessad", label: "广告记录" },
        { key: "adlog", label: "其他广告" },
        { key: "bill", label: "流水" },
        { key: "risk", label: "风险详情", type: "warning" },
        { key: "login", label: "登录轨迹" },
        {
          key: "ban",
          label:
            row.banned_time && row.banned_time * 1000 > Date.now()
              ? "解除封禁"
              : "封禁",
          type: "danger",
        },
      ];
    },
    getAvatar(avatar) {
      if (!avatar) {
        return "";
      }
      if (avatar.indexOf("http") >= 0 || avatar.indexOf("api") >= 0) {
        return avatar;
      }
      return `/api/${avatar}`;
    },
    formatTime(value) {
      return value ? this.$fnc.getTimeFormat(value, "ymdhms") : "--";
    },
    riskTagType(level) {
      if (level === "high") return "danger";
      if (level === "medium") return "warning";
      if (level === "low") return "primary";
      return "success";
    },
    riskLevelText(level) {
      if (level === "high") return "高风险";
      if (level === "medium") return "中风险";
      if (level === "low") return "低风险";
      return "安全";
    },
    formatRiskTags(tags) {
      if (!Array.isArray(tags) || !tags.length) {
        return "--";
      }
      return tags.join(" / ");
    },
    async getOverviewStats() {
      this.overviewLoading = true;
      try {
        const data = await this.$api.getUser.getOverviewStats();
        this.overviewStats = data.data || emptyOverview();
      } finally {
        this.overviewLoading = false;
      }
    },
    async getUserList() {
      this.loading = true;
      try {
        const data = await this.$api.getUser.getUserList({
          ...this.paginationForm,
          ...this.queryForm,
        });
        this.list = Array.isArray(data.data) ? data.data : [];
        this.total = data.total || 0;
      } finally {
        this.loading = false;
      }
    },
    async ensureAppOptions() {
      if (Array.isArray(this.$store.state.appList) && this.$store.state.appList.length) {
        return;
      }
      await this.$store.dispatch("getAppList");
    },
    handleCurrentChange(page) {
      this.paginationForm.page = page;
      this.getUserList();
    },
    handleSizeChange(size) {
      this.paginationForm.page_size = size;
      this.paginationForm.page = 1;
      this.getUserList();
    },
    getSearch(form) {
      this.queryForm = form || {};
      this.paginationForm.page = 1;
      this.getUserList();
    },
    handleActionCommand(command, row) {
      if (command === "detail") {
        this.openDetail(row.id);
        return;
      }
      if (command === "accessad") {
        this.openAccessadRecord(row.id);
        return;
      }
      if (command === "adlog") {
        this.openAdLogRecord(row.id);
        return;
      }
      if (command === "bill") {
        this.openBillRecord(row.id);
        return;
      }
      if (command === "risk") {
        this.openRiskDetail(row.id);
        return;
      }
      if (command === "login") {
        this.openLoginRecord(row.id);
        return;
      }
      if (command === "ban") {
        this.toggleBan(row);
      }
    },
    openDetail(id) {
      this.currentUserId = id;
      this.showUserDialog = true;
    },
    showEditDialog() {
      this.$refs.userDialogRef.openDialog("新增用户");
    },
    normalizeSavePayload(formData, type) {
      const basePayload = {
        username: formData.username,
        nickname: formData.nickname,
        app_id: formData.app_id,
      };

      if (type !== "edit") {
        return basePayload;
      }

      return {
        ...basePayload,
        id: formData.id,
        open_id: formData.open_id,
        android_id: formData.android_id,
        ip: formData.ip,
        session_id: formData.session_id,
        phone_version: formData.phone_version,
        sdk_version: formData.sdk_version,
        phone_manufacturer: formData.phone_manufacturer,
        banned_time: formData.banned_time
          ? Math.floor(Number(formData.banned_time) / 1000)
          : "",
        channel_id: formData.channel_id,
        address: formData.address,
      };
    },
    async submitForm(formData, type) {
      const payload = this.normalizeSavePayload(formData, type);
      const data =
        type === "edit"
          ? await this.$api.getUser.saveUserInfo(payload)
          : await this.$api.getUser.addUser(payload);

      if (data && data.code === 200) {
        this.$message.success("保存成功");
        this.getUserList();
      }
    },
    openLoginRecord(id) {
      this.$refs.loginListRef.open(id);
    },
    openRiskDetail(id) {
      this.$refs.riskDialogRef.open(id);
    },
    openBillRecord(id) {
      this.$refs.billListRef.open(id);
    },
    openAccessadRecord(id) {
      this.$refs.adListRef.openDialog(id);
    },
    openAdLogRecord(id) {
      this.$refs.adLogListRef.openDialog(id);
    },
    toggleBan(row) {
      const active = row.banned_time && row.banned_time * 1000 > Date.now();
      if (active) {
        this.$confirm("确认解除该用户封禁吗？", "提示", {
          type: "warning",
        }).then(async () => {
          const data = await this.$api.getUser.bannedUser({
            user_id: row.id,
            time: 0,
          });
          if (data.code === 200) {
            this.$message.success("已解除封禁");
            this.getUserList();
          }
        });
        return;
      }

      this.$prompt("请输入封禁时长（秒）", "封禁用户", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        inputPattern: /^\d+$/,
        inputErrorMessage: "请输入正整数秒数",
      }).then(async ({ value }) => {
        const data = await this.$api.getUser.bannedUser({
          user_id: row.id,
          time: value,
        });
        if (data.code === 200) {
          this.$message.success("封禁成功");
          this.getUserList();
        }
      });
    },
  },
  async created() {
    await this.ensureAppOptions();
    this.getOverviewStats();
    this.getUserList();
  },
};
</script>

<style lang="less" scoped>
.overview-grid {
  margin-bottom: 18px;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 14px;
}

.overview-card {
  padding: 16px 18px;
  border-radius: 16px;
  background: linear-gradient(180deg, #f7fbff, #eef5ff);
  border: 1px solid #dde8f8;
}

.overview-card__label {
  color: #6f84a8;
  font-size: 13px;
}

.overview-card__value {
  margin-top: 10px;
  color: #203864;
  font-size: 28px;
  font-weight: 600;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
}

.user-main__title {
  font-weight: 600;
  color: #203864;
}

.info-lines {
  line-height: 1.7;
  color: #516b96;
}

.risk-line {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.risk-score {
  color: #6f84a8;
  font-size: 12px;
}

.risk-tags-line {
  color: #7b8faa;
  font-size: 12px;
}

.user-detail-dialog /deep/ .el-dialog__body {
  padding-top: 10px;
}

/deep/ .el-table .el-table__cell {
  padding: 8px 0;
}
</style>
